import { useState, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { Button, Checkbox, Dialog, Field, Input, Select } from '@/components/ui';
import { useRedirectChange } from '@/hooks/useDomains';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import type { RedirectView, SiteZoneView } from '@/types/api';

/** Permanent first: what a moved page almost always wants. */
export const REDIRECT_CODE_LABELS: Record<number, string> = {
  301: 'Постоянный · 301',
  308: 'Постоянный · 308',
  302: 'Временный · 302',
  307: 'Временный · 307',
};

type Code = '301' | '308' | '302' | '307';

const CODE_OPTIONS: { value: Code; label: string }[] = [
  { value: '301', label: 'Постоянный (301) — страница переехала насовсем' },
  { value: '308', label: 'Постоянный с сохранением метода (308)' },
  { value: '302', label: 'Временный (302) — страница скоро вернётся' },
  { value: '307', label: 'Временный с сохранением метода (307)' },
];

/** Adding or changing one Bulk Redirect; saved in 2d2s the moment it is confirmed. */
export function RedirectDialog({
  siteId,
  zone,
  redirect,
  onClose,
}: {
  readonly siteId: string;
  readonly zone: SiteZoneView;
  /** Null to add a new one. */
  readonly redirect: RedirectView | null;
  readonly onClose: () => void;
}): ReactNode {
  const [source, setSource] = useState(redirect?.sourceUrl ?? '');
  const [target, setTarget] = useState(redirect?.targetUrl ?? '');
  const [code, setCode] = useState<Code>(String(redirect?.statusCode ?? 301) as Code);
  const [query, setQuery] = useState(redirect?.preserveQueryString ?? true);
  const [subpaths, setSubpaths] = useState(redirect?.subpathMatching ?? false);
  const [suffix, setSuffix] = useState(redirect?.preservePathSuffix ?? false);
  const [subdomains, setSubdomains] = useState(redirect?.includeSubdomains ?? false);

  const change = useRedirectChange(siteId, zone.domainId ?? '', {
    onSuccess: () => {
      toast.success(redirect ? 'Редирект изменён' : 'Редирект добавлен', 'Применяется в течение минуты');
      onClose();
    },
  });
  const field = change.isError ? errorField(change.error) : null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    const body = {
      sourceUrl: source.trim(),
      targetUrl: target.trim(),
      statusCode: Number(code) as 301 | 302 | 307 | 308,
      preserveQueryString: query,
      subpathMatching: subpaths,
      preservePathSuffix: subpaths && suffix,
      includeSubdomains: subdomains,
    };
    change.mutate(redirect ? { kind: 'update', redirectId: redirect.id, redirect: body } : { kind: 'create', redirect: body });
  }

  const error = (which: string) => (field === which ? { error: errorText(change.error) } : {});

  return (
    <Dialog
      open
      title={redirect ? 'Изменить редирект' : 'Новый редирект'}
      description={zone.apex}
      onClose={() => (change.isPending ? undefined : onClose())}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={change.isPending}>
            Отмена
          </Button>
          <Button variant="primary" icon={Save} busy={change.isPending} type="submit" form="redirect" disabled={!source.trim() || !target.trim()}>
            Сохранить
          </Button>
        </>
      }
    >
      <form id="redirect" className="stack" onSubmit={submit} noValidate>
        <Field label="Откуда" htmlFor="redirect-source" hint={`Адрес на ${zone.apex}, без https:// — например ${zone.apex}/old-page`} {...error('sourceUrl')}>
          <Input
            id="redirect-source"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            placeholder={`${zone.apex}/old-page`}
            invalid={field === 'sourceUrl'}
            autoComplete="off"
            inputMode="url"
          />
        </Field>
        <Field label="Куда" htmlFor="redirect-target" hint="Полный адрес, с https://" {...error('targetUrl')}>
          <Input
            id="redirect-target"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            placeholder={`https://${zone.apex}/new-page`}
            invalid={field === 'targetUrl'}
            autoComplete="off"
            inputMode="url"
          />
        </Field>
        <Field label="Тип" htmlFor="redirect-code" {...error('statusCode')}>
          <Select id="redirect-code" label="Тип" value={code} onChange={setCode} options={CODE_OPTIONS} />
        </Field>
        <Checkbox checked={query} onChange={setQuery} label="Сохранять параметры запроса" hint="?utm_source=… и прочее после «?» переходят на новый адрес" />
        <Checkbox checked={subpaths} onChange={setSubpaths} label="Также для всех адресов ниже этого пути" hint="/old-section/любая-страница тоже будет перенаправлена" />
        <Checkbox
          checked={subpaths && suffix}
          onChange={setSuffix}
          disabled={!subpaths}
          label="Переносить остаток пути"
          hint="/old-section/page → /new-section/page, а не всё на один адрес"
        />
        <Checkbox checked={subdomains} onChange={setSubdomains} label="Также для поддоменов" hint={`Например, для www.${zone.apex}`} />
        {change.isError && !field ? (
          <p className="field-error" role="alert">
            {errorText(change.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
